import React from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Youtube, 
  Instagram, 
  Linkedin,
  Heart,
  Code
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';

const FooterWrapper = styled.footer`
  background: var(--gray-900);
  color: var(--gray-0);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterContainer = styled(Container)`
  padding: var(--space-16) 0 var(--space-8);
  position: relative;
  z-index: 1;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: var(--space-12);
  margin-bottom: var(--space-12);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-8);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--space-10);
  }
`;

const BrandSection = styled.div`
  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    
    @media (max-width: 640px) {
      justify-content: center;
    }
    
    .logo-icon {
      width: 48px;
      height: 48px;
      background: var(--gradient-primary);
      border-radius: var(--radius-xl);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--gray-0);
    }
    
    .brand-text {
      font-size: var(--text-2xl);
      font-weight: 800;
      background: var(--gradient-primary);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
  
  .description {
    color: var(--gray-400);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-6);
    font-size: var(--text-base);
  }
  
  .contact-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-8);
    
    @media (max-width: 640px) {
      align-items: center;
    }
  }
  
  .contact-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    color: var(--gray-300);
    font-size: var(--text-sm);
    
    .icon {
      color: var(--primary-400);
      flex-shrink: 0;
    }
    
    a {
      color: var(--gray-300);
      text-decoration: none;
      transition: color var(--transition-fast);
      
      &:hover {
        color: var(--primary-400);
      }
    }
  }
`;

const FooterSection = styled.div`
  h4 {
    font-size: var(--text-lg);
    font-weight: 700;
    margin-bottom: var(--space-6);
    color: var(--gray-0);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -var(--space-2);
      left: 0;
      width: 40px;
      height: 2px;
      background: var(--gradient-primary);
      
      @media (max-width: 640px) {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  
  li {
    a {
      color: var(--gray-400);
      text-decoration: none;
      font-size: var(--text-sm);
      transition: all var(--transition-fast);
      
      &:hover {
        color: var(--primary-400);
        transform: translateX(4px);
      }
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--space-4);
  
  @media (max-width: 640px) {
    justify-content: center;
  }
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: var(--gray-800);
    border: 1px solid var(--gray-700);
    border-radius: var(--radius-xl);
    color: var(--gray-400);
    transition: all var(--transition-normal);
    
    &:hover {
      background: var(--gradient-primary);
      color: var(--gray-0);
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
      border-color: transparent;
    }
  }
`;

const FooterBottom = styled.div`
  padding-top: var(--space-6);
  border-top: 1px solid var(--gray-800);
  text-align: center;
  
  .copyright {
    color: var(--gray-400);
    font-size: var(--text-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    
    .heart {
      color: var(--red-500);
      animation: heartbeat 2s ease-in-out infinite;
    }
  }
`;

const services = [
  'UI/UX Design',
  'Web Development', 
  'Database Management',
  'AI/ML Solutions',
  'IoT Development'
];

const quickLinks = [
  { name: 'About Us', href: '/about' },
  { name: 'Services', href: '/service' },
  { name: 'Career Programs', href: '/career-paths' },
  { name: 'Internships', href: '/internships' },
  { name: 'Contact Us', href: '/contact' }
];

const Footer = () => {
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterGrid>
          <BrandSection>
            <div className="brand">
              <div className="logo-icon">
                <Code size={24} />
              </div>
              <span className="brand-text">Willovate</span>
            </div>
            
            <p className="description">
              Transforming ideas into powerful software solutions. We build scalable, 
              secure, and innovative applications that drive business growth.
            </p>
            
            <div className="contact-info">
              <div className="contact-item">
                <Mail size={16} className="icon" />
                <a href="mailto:admin@willovate.in">admin@willovate.in</a>
              </div>
              <div className="contact-item">
                <Phone size={16} className="icon" />
                <a href="tel:+919356603646">+91 9356603646</a>
              </div>
              <div className="contact-item">
                <MapPin size={16} className="icon" />
                <span>Nagpur, Maharashtra, India</span>
              </div>
            </div>
            
            <SocialLinks>
              <a href="https://linkedin.com/company/willovate" target="_blank" rel="noopener noreferrer">
                <Linkedin size={20} />
              </a>
              <a href="https://instagram.com/willovate_" target="_blank" rel="noopener noreferrer">
                <Instagram size={20} />
              </a>
              <a href="https://www.youtube.com/@willovate" target="_blank" rel="noopener noreferrer">
                <Youtube size={20} />
              </a>
            </SocialLinks>
          </BrandSection>
          
          <FooterSection>
            <h4>Our Expertise</h4>
            <ul>
              {services.map((service, index) => (
                <li key={index}>
                  <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </FooterSection>
          
          <FooterSection>
            <h4>Quick Links</h4>
            <ul>
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.href}>{link.name}</a>
                </li>
              ))}
            </ul>
          </FooterSection>
        </FooterGrid>
        
        <FooterBottom>
          <div className="copyright">
            <span>© 2025 Willovate. Made with</span>
            <Heart size={16} className="heart" />
            <span>in India</span>
          </div>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
